/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SIGNATURE FORMATTER
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Handles proper formatting of email signatures for display and sync
 * Ensures signatures maintain proper line breaks and formatting
 */

/**
 * Formats a signature for display in the UI (plain text with preserved line breaks)
 */
export function formatSignatureForDisplay(signature: string | null | undefined): string {
  if (!signature) return ''
  
  // Remove HTML tags but preserve line breaks
  let formatted = signature
    .replace(/<br\s*\/?>/gi, '\n') // Convert <br> tags to newlines
    .replace(/<\/p>/gi, '\n') // Convert </p> tags to newlines
    .replace(/<p[^>]*>/gi, '') // Remove opening <p> tags
    .replace(/<[^>]*>/g, '') // Remove all other HTML tags
    .replace(/&nbsp;/g, ' ') // Convert non-breaking spaces to regular spaces
    .replace(/&amp;/g, '&') // Convert HTML entities
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim()
  
  return formatted
}

/**
 * Formats a signature for sync to Smartlead (preserves formatting as HTML)
 */
export function formatSignatureForSync(signature: string | null | undefined): string {
  if (!signature) return ''
  
  // If signature already contains HTML, preserve it
  if (signature.includes('<') && signature.includes('>')) {
    return signature.trim()
  }
  
  // Convert plain text to HTML with proper line breaks
  const htmlSignature = signature
    .replace(/\n/g, '<br>') // Convert newlines to <br> tags
    .replace(/\r\n/g, '<br>') // Convert Windows line endings
    .replace(/\r/g, '<br>') // Convert Mac line endings
    .trim()
  
  return htmlSignature
}

/**
 * Normalizes a signature for comparison (removes formatting differences)
 */
export function normalizeSignatureForComparison(signature: string | null | undefined): string {
  if (!signature) return ''
  
  return signature
    .replace(/<br\s*\/?>/gi, '\n') // Convert <br> tags to newlines
    .replace(/<\/p>/gi, '\n') // Convert </p> tags to newlines
    .replace(/<p[^>]*>/gi, '') // Remove opening <p> tags
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/&nbsp;/g, ' ') // Convert non-breaking spaces
    .replace(/&amp;/g, '&') // Convert HTML entities
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim()
}

/**
 * Checks if two signatures are equivalent (ignoring formatting differences)
 */
export function areSignaturesEquivalent(sig1: string | null | undefined, sig2: string | null | undefined): boolean {
  const normalized1 = normalizeSignatureForComparison(sig1)
  const normalized2 = normalizeSignatureForComparison(sig2)
  return normalized1 === normalized2
}



